import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { EmptyState, ErrorState, PermissionDeniedState } from "../components/states";
import { OrganisationSpendSummary } from "../components/organisation/OrganisationSpendSummary";
import { OrganisationPersonUsage } from "../components/organisation/OrganisationPersonUsage";
import { OverviewHero } from "../components/overview/OverviewHero";
import { OverviewLoading } from "../components/overview/OverviewLoading";
import { PeopleTrendPanel } from "../components/overview/PeopleTrendPanel";
import { SeatUsePanel } from "../components/overview/SeatUsePanel";
import { SpendTrendPanel } from "../components/overview/SpendTrendPanel";
import { addMonths, capexSplit, chartMonths, financialYearComparison, financialYearStart, monthsToRequest, overviewBudget, overviewHeadline } from "../components/overview/overviewFigures";
import overviewStyles from "../components/overview/Overview.module.css";
import { LayoutSwitcher, SectionLayoutProvider } from "../components/layout/SectionLayout";
import { useSelectedPeriod } from "../hooks/useSelectedPeriod";
import {
  fetchOrganisationBudgets,
  fetchOrganisationDailySpend,
  fetchOrganisationMonthlyBudgets,
  fetchOrganisationPersonUsage,
  fetchOrganisationSpend,
  fetchOrganisationTrend,
  type BudgetReportResponse,
  type OrganisationDailySpendResponse,
  type OrganisationMonthlyBudgetResponse,
  type OrganisationPersonUsageResponse,
  type OrganisationSpendResponse,
  type OrganisationTrendResponse,
} from "../api/organisationReporting";
import { unchangedOr, usePolling } from "../hooks/usePolling";

const OrgConsumptionPermission = "consumption.read.org";
const OthersConsumptionPermission = "consumption.read.others";
const AdministerPermission = "entitlement.administer";

/** Everything the page draws, fetched together and committed together. */
interface OrganisationFigures {
  trend: OrganisationTrendResponse;
  spend: OrganisationSpendResponse;
  daily: OrganisationDailySpendResponse | null;
  previousDaily: OrganisationDailySpendResponse | null;
  monthlyBudgets: OrganisationMonthlyBudgetResponse[] | null;
  budgetReport: BudgetReportResponse | null;
  personUsage: OrganisationPersonUsageResponse | null;
}

type PageState =
  | { kind: "loading" }
  | { kind: "loaded"; figures: OrganisationFigures }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/**
 * The Organization page (phase 14, `AIA-81`, `UR-L-01`–`UR-L-05`, merged with the AI overview): the organisation's use of AI for
 * the people who own its spend. The hero gives the month's spend against last month and the budget, the year so far, the people
 * using AI and the few things worth a look; the Budgets section follows, then the spend and the people over the last twelve
 * months and how much of what is paid for is used, then the month's spend by tool, model, division, department, group and (for a
 * viewer who may see names) person. Gated on `consumption.read.org` (`QR-06`); the by-person section also needs
 * `consumption.read.others`, and is never requested without it.
 */
export function OrganisationPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [periodStart] = useSelectedPeriod();

  const hasPermission = snapshot.status === "signed-in" && snapshot.permissions.includes(OrgConsumptionPermission);
  const canSeePeople = snapshot.status === "signed-in" && snapshot.permissions.includes(OthersConsumptionPermission);
  const canAdminister = snapshot.status === "signed-in" && snapshot.permissions.includes(AdministerPermission);

  const load = useCallback(
    // A quiet load refreshes the figures in place, without the loading layout, and keeps them if it fails.
    async (signal?: AbortSignal, quiet = false) => {
      if (!quiet) {
        setState({ kind: "loading" });
      }
      const yearStart = financialYearStart(periodStart);
      // Committing some figures before others would draw the charts against the previous period's series, then redraw them.
      const [trend, spend, daily, previousDaily, monthlyBudgets, budgetReport, personUsage] = await Promise.all([
        // Back to the start of the last financial year, so this year can be set against it; the charts show the last twelve months.
        fetchOrganisationTrend(periodStart, monthsToRequest(periodStart), signal),
        fetchOrganisationSpend(periodStart, signal),
        fetchOrganisationDailySpend(periodStart, signal),
        // Last month's daily series gives each breakdown its change on last month.
        fetchOrganisationDailySpend(addMonths(periodStart, -1), signal),
        // The budget recorded for each month of this financial year, for the month and year gauges.
        fetchOrganisationMonthlyBudgets(yearStart, addMonths(yearStart, 11), signal),
        // The month's budget report (GH-135, R-29): the Budgets section, and the Capex split while Capex is not counted.
        fetchOrganisationBudgets(periodStart, signal),
        canSeePeople ? fetchOrganisationPersonUsage(periodStart, signal) : null,
      ]);
      if ([trend, spend, daily, previousDaily, monthlyBudgets, budgetReport, personUsage].some((result) => result?.kind === "aborted")) {
        return;
      }

      // The trend and the month's spend are the page; the rest only add to it, so a failure there leaves their sections out.
      const failed = trend.kind !== "ok" ? trend : spend.kind !== "ok" ? spend : null;
      if (quiet && failed) {
        return;
      }

      if (failed?.kind === "denied") {
        setState({ kind: "denied" });
        return;
      }
      if (failed?.kind === "error") {
        setState({ kind: "error", message: failed.message });
        return;
      }
      if (trend.kind !== "ok" || spend.kind !== "ok") {
        return;
      }

      const next: PageState = {
        kind: "loaded",
        figures: {
          trend: trend.response,
          spend: spend.response,
          daily: daily.kind === "ok" ? daily.response : null,
          previousDaily: previousDaily.kind === "ok" ? previousDaily.response : null,
          monthlyBudgets: monthlyBudgets.kind === "ok" ? monthlyBudgets.response.months : null,
          // A budget report without its unit list (a reply that is not the report) is treated as not loaded, so its section is left out.
          budgetReport: budgetReport.kind === "ok" && Array.isArray(budgetReport.response.units) ? budgetReport.response : null,
          personUsage: personUsage?.kind === "ok" ? personUsage.response : null,
        },
      };
      setState((previous) => unchangedOr(previous, next));
    },
    [periodStart, canSeePeople],
  );

  useEffect(() => {
    if (!hasPermission) {
      return;
    }

    // Abort on cleanup so an effect re-run (including React StrictMode's dev-only double-invoke)
    // cancels the superseded request instead of leaving it to complete and issue a duplicate fetch.
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [hasPermission, load]);

  usePolling(() => load(undefined, true), hasPermission && state.kind === "loaded");

  if (!hasPermission || state.kind === "denied") {
    return <PermissionDeniedState title={t("organisation.denied.title")} description={t("organisation.denied.description")} />;
  }

  return (
    <SectionLayoutProvider>
      <section aria-labelledby="organisation-heading">
        <h1 id="organisation-heading" className="visually-hidden">{t("organisation.title")}</h1>
        {/* At the top, outside the loading and loaded views, so it stays in place while the figures arrive. */}
        <LayoutSwitcher />
        {/* Loading and a failed load share the loaded page's layout, so the figures arrive in place and a retry does not swap layouts. */}
        {(state.kind === "loading" || state.kind === "error") && (
          <OverviewLoading
            withPeople={canSeePeople}
            error={state.kind === "error" ? <ErrorState title={t("organisation.errorTitle")} description={state.message} onRetry={() => void load()} /> : undefined}
          />
        )}
        {state.kind === "loaded" && <OrganisationBody figures={state.figures} permissions={{ administer: canAdminister, seePeople: canSeePeople }} />}
      </section>
    </SectionLayoutProvider>
  );
}

function OrganisationBody({ figures, permissions }: { figures: OrganisationFigures; permissions: { administer: boolean; seePeople: boolean } }): React.JSX.Element {
  const { t } = useTranslation();
  const { trend, spend, daily, previousDaily, monthlyBudgets, budgetReport, personUsage } = figures;
  const headline = overviewHeadline(trend, daily?.projection?.projectedTotal ?? null);
  if (headline === null) {
    return <EmptyState title={t("organisation.emptyTitle")} description={t("organisation.emptyDescription")} />;
  }

  const lastTwelve: OrganisationTrendResponse = { ...trend, months: trend.months.slice(-chartMonths) };
  const financialYear = financialYearComparison(trend);

  return (
    <OrganisationSpendSummary
      response={spend}
      dailySpend={daily}
      previousDailySpend={previousDaily}
      trend={trend}
      budgets={budgetReport}
      permissions={permissions}
      hero={(insights) => (
        <OverviewHero
          headline={headline}
          financialYear={financialYear}
          budget={overviewBudget(headline, financialYear, monthlyBudgets, new Date(trend.generatedAt))}
          currency={trend.currency}
          capex={capexSplit(budgetReport, headline.spend)}
          insights={insights}
        />
      )}
      trends={
        <div className={`${overviewStyles.grid} ${overviewStyles.trends}`}>
          <SpendTrendPanel trend={lastTwelve} projected={headline.projected} />
          <PeopleTrendPanel trend={lastTwelve} />
          <SeatUsePanel trend={lastTwelve} month={headline.month} />
        </div>
      }
    >
      {personUsage && <OrganisationPersonUsage response={personUsage} />}
    </OrganisationSpendSummary>
  );
}
